import React, { useEffect, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { friendlyApiError } from '../utils/apiError';
import { useNavigate } from 'react-router-dom';
import AdminNavTabs from '../components/AdminNavTabs';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

export default function AdminLocations() {
  const navigate = useNavigate();
  const statusRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [locations, setLocations] = useState([]);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
    },
    phone: '',
    email: '',
    timezone: 'America/New_York',
    is_active: true,
    is_primary: false,
  });

  useEffect(() => {
    if (!statusMessage && !error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [statusMessage, error]);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'Locations');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
          return;
        }

        setUsername(user.username || user.attributes?.email || 'Admin');
        await fetchLocations();
      } catch {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchLocations = async () => {
    if (!API_BASE_URL) return;
    try {
      setError('');
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/locations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load locations');

      const list = Array.isArray(data?.locations) ? data.locations : [];
      setLocations(list);
    } catch (e) {
      setError(e?.message || 'Failed to load locations');
    }
  };

  const startCreate = () => {
    setEditingLocation(null);
    setFormData({
      name: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
      },
      phone: '',
      email: '',
      timezone: 'America/New_York',
      is_active: true,
      is_primary: false,
    });
    setShowForm(true);
  };

  const startEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location?.name || '',
      address: {
        street: location?.address?.street || '',
        city: location?.address?.city || '',
        state: location?.address?.state || '',
        zip: location?.address?.zip || '',
        country: location?.address?.country || 'US',
      },
      phone: location?.phone || '',
      email: location?.email || '',
      timezone: location?.timezone || 'America/New_York',
      is_active: location?.is_active === 'true' || location?.is_active === true,
      is_primary: location?.is_primary === 'true' || location?.is_primary === true,
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingLocation(null);
    setFormData({
      name: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
      },
      phone: '',
      email: '',
      timezone: 'America/New_York',
      is_active: true,
      is_primary: false,
    });
  };

  const saveLocation = async () => {
    if (!API_BASE_URL) return;
    if (saving) return;

    const name = String(formData.name || '').trim();
    if (!name) {
      setError('Location name is required');
      return;
    }

    // Validate address fields
    const street = String(formData.address?.street || '').trim();
    if (!street) {
      setError('Street address is required');
      return;
    }

    const city = String(formData.address?.city || '').trim();
    if (!city) {
      setError('City is required');
      return;
    }

    const state = String(formData.address?.state || '').trim();
    if (!state) {
      setError('State is required');
      return;
    }

    const zip = String(formData.address?.zip || '').trim();
    if (!zip) {
      setError('ZIP code is required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const payload = {
        name: formData.name,
        address: formData.address,
        phone: formData.phone,
        email: formData.email,
        timezone: formData.timezone,
        is_active: formData.is_active,
      };

      // Include is_primary for both create and update
      // Backend will validate whether the change is allowed
      payload.is_primary = formData.is_primary;

      let res;
      if (editingLocation) {
        // Update existing location
        res = await fetch(`${API_BASE_URL}/admin/locations/${editingLocation.location_id}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        // Create new location
        res = await fetch(`${API_BASE_URL}/admin/locations`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Save failed'));

      setStatusMessage(editingLocation ? 'Location updated successfully' : 'Location created successfully');
      cancelForm();
      await fetchLocations();
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        window.scrollTo(0, 0);
      }
      setSaving(false);
    }
  };

  const deleteLocation = async (location) => {
    if (!API_BASE_URL) return;
    if (!location) return;

    // Prevent deletion of primary location
    if (location.is_primary === 'true' || location.is_primary === true) {
      setError('Cannot delete primary location');
      return;
    }

    if (!window.confirm(`Delete location "${location.name}"? This action cannot be undone.`)) return;

    try {
      setSaving(true);
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/locations/${location.location_id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Delete failed'));

      setStatusMessage('Location deleted successfully');
      await fetchLocations();
    } catch (e) {
      setError(e?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading locations...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard admin-locations-page">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-map-marker-alt"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>Locations</h1>
            <p>Manage store locations</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username || 'Admin'}
          </span>
          <button onClick={() => Auth.signOut().then(() => navigate('/admin/login'))} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {error ? (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        ) : null}

        {statusMessage ? (
          <div className="alert alert-success" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
            <i className="fas fa-check-circle"></i>
            {statusMessage}
          </div>
        ) : null}

        {showForm ? (
          <div className="products-list-container">
            <details open>
              <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>
                {editingLocation ? 'Edit Location' : 'Create Location'}
              </summary>

              <div style={{ display: 'grid', gap: 14, marginTop: 14 }}>
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div className="form-group">
                    <label>Location Name *</label>
                    <input
                      value={formData.name}
                      onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Main Store"
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      value={formData.phone}
                      onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                      placeholder="store@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Timezone</label>
                    <select
                      value={formData.timezone}
                      onChange={(e) => setFormData((p) => ({ ...p, timezone: e.target.value }))}
                    >
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                      <option value="America/Phoenix">Arizona Time</option>
                      <option value="America/Anchorage">Alaska Time</option>
                      <option value="Pacific/Honolulu">Hawaii Time</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Street Address *</label>
                  <input
                    value={formData.address.street}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, address: { ...p.address, street: e.target.value } }))
                    }
                    placeholder="123 Main St"
                    required
                  />
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14 }}>
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      value={formData.address.city}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, address: { ...p.address, city: e.target.value } }))
                      }
                      placeholder="New York"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>State *</label>
                    <input
                      value={formData.address.state}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, address: { ...p.address, state: e.target.value } }))
                      }
                      placeholder="NY"
                      maxLength={2}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>ZIP *</label>
                    <input
                      value={formData.address.zip}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, address: { ...p.address, zip: e.target.value } }))
                      }
                      placeholder="10001"
                      required
                    />
                  </div>
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14 }}>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.checked }))}
                      />
                      Active (location is open for business)
                    </label>
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={formData.is_primary}
                        onChange={(e) => setFormData((p) => ({ ...p, is_primary: e.target.checked }))}
                        disabled={editingLocation && (editingLocation.is_primary === 'true' || editingLocation.is_primary === true)}
                      />
                      Primary Location (default for customers)
                      {editingLocation && (editingLocation.is_primary === 'true' || editingLocation.is_primary === true) ? (
                        <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: 8 }}>
                          (already primary)
                        </span>
                      ) : null}
                    </label>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-primary" onClick={saveLocation} disabled={saving}>
                    {saving ? 'Saving…' : editingLocation ? 'Update Location' : 'Create Location'}
                  </button>
                  <button className="btn" onClick={cancelForm} disabled={saving}>
                    Cancel
                  </button>
                </div>
              </div>
            </details>
          </div>
        ) : (
          <div className="products-list-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h2 style={{ fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95', margin: 0 }}>Locations</h2>
              <button className="btn btn-primary" onClick={startCreate} disabled={saving}>
                <i className="fas fa-plus"></i> Create Location
              </button>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="products-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Address</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.map((location) => {
                    const addr = location?.address || {};
                    const addressStr = [addr.street, addr.city, addr.state, addr.zip]
                      .filter(Boolean)
                      .join(', ');

                    return (
                      <tr key={location.location_id}>
                        <td style={{ fontWeight: 800 }}>
                          {location.name}
                          {(location.is_primary === 'true' || location.is_primary === true) ? (
                            <span
                              style={{
                                marginLeft: 8,
                                padding: '2px 8px',
                                background: '#4c1d95',
                                color: 'white',
                                borderRadius: 4,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                              }}
                            >
                              PRIMARY
                            </span>
                          ) : null}
                        </td>
                        <td>{addressStr || '—'}</td>
                        <td>{location.phone || '—'}</td>
                        <td>
                          <span
                            style={{
                              padding: '4px 8px',
                              borderRadius: 4,
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              background: (location.is_active === 'true' || location.is_active === true) ? '#d1fae5' : '#fee2e2',
                              color: (location.is_active === 'true' || location.is_active === true) ? '#065f46' : '#991b1b',
                            }}
                          >
                            {(location.is_active === 'true' || location.is_active === true) ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', gap: 10 }}>
                            <button className="btn" onClick={() => startEdit(location)} disabled={saving}>
                              Edit
                            </button>
                            <button
                              className="btn"
                              onClick={() => deleteLocation(location)}
                              disabled={saving || location.is_primary === 'true' || location.is_primary === true}
                              title={(location.is_primary === 'true' || location.is_primary === true) ? 'Cannot delete primary location' : 'Delete location'}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {locations.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ padding: 16, textAlign: 'center', opacity: 0.75 }}>
                        No locations yet. Create your first location to get started.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
